import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorDirective, ErrorsDirective } from '@shared/directives/errors';
import { MomentPipe } from '@shared/pipes/moment';
import { UtilService } from '@shared/services/util.service';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { TenDateAdapter } from '@shared/shared/helpers/ten-date-adapter';
import { ICountry } from '@shared/shared/interfaces/country.interfaces';
import { IMemberProfileTraveller } from '@shared/shared/interfaces/member-profile-traveller.interfaces';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as MemberProfileStore from '@shared/store/member-profile';
import { Angulartics2Segment } from 'angulartics2';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CountryService } from '../../../services/country.service';

const noSpecialCharsValidator = Validators.pattern(/^[^;<>()*]+$/i);

const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'L',
  },
  display: {
    dateInput: localStorage.getItem('ten-language-settings')?.match(/zh-/) ? 'LL' : 'L',
    monthYearLabel: 'MM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'ten-traveller-form',
  templateUrl: './traveller-form.component.html',
  styleUrls: ['../popup-form-base.scss', './traveller-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MomentPipe,
    {
      provide: DateAdapter,
      useClass: TenDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  ],
  standalone: true,
  imports: [
    NgClass,
    MatLegacySelectModule,
    ReactiveFormsModule,
    TranslateModule,
    NgIf,
    MatLegacyFormFieldModule,
    ErrorDirective,
    ErrorsDirective,
    MatLegacyInputModule,
    MatDatepickerModule,
    MatLegacyAutocompleteModule,
    MatLegacyButtonModule,
    NgForOf,
  ],
})
export class TravellerFormComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() public memberUrl: string;
  @Input() public pastDataId?: number;
  @Input() public minEligibleAge: Array<number> = [0, 150];
  @Input() public maxAllowedDate: string;
  @Input() public minAllowedDate: string;
  @Output() public closed = new EventEmitter<any>();
  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    this.innerWidth = window.innerWidth;
  }

  public componentData: { layout: string };
  public member_profile_travellers$: Observable<IMemberProfileTraveller[]> = this.ngrxStore.select(
    MemberProfileStore.getMemberProfileTravellersFeatureState
  );
  public travellerFormGroup: UntypedFormGroup;
  private unsubscribe$ = new Subject<void>();
  public countries: Array<ICountry>;
  public invalidCitizenship = false;
  public filteredCountries: Array<ICountry>;
  public newTraveller: boolean;
  public innerWidth: number;
  public maxDate: string;
  public minDate: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private countryService: CountryService,
    private datePipe: DatePipe,
    private ngrxStore: Store<MemberProfileStore.MemberProfileState>,
    private util: UtilService,
    private angulartics2Segment: Angulartics2Segment,
    siteConfService: SiteConfigService
  ) {
    super(siteConfService, 'travellerDetails');
    this.innerWidth = window.innerWidth;

    this.travellerFormGroup = this.formBuilder.group({
      title: ['', Validators.required],
      first_name: ['', [Validators.required, noSpecialCharsValidator]],
      last_name: ['', [Validators.required, noSpecialCharsValidator]],
      birth_date: [null, Validators.required],
      citizenship: ['', Validators.required],
      country_url: ['', Validators.required],
      gender: ['', Validators.required],
    });

    this.countryService
      .getCountries()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.countries = data;
        this.filterAutocomplete();
      });
  }

  public ngOnInit(): void {
    this.minDate = this.datePipe.transform(
      moment().subtract(this.minEligibleAge[1], 'years').add(1, 'days').toDate(),
      'yyyy-MM-dd'
    );

    this.maxDate = this.datePipe.transform(moment().subtract(this.minEligibleAge[0], 'years').toDate(), 'yyyy-MM-dd');

    this.newTraveller = this.pastDataId === undefined;
    if (!this.newTraveller) {
      this.member_profile_travellers$
        .pipe(
          filter((value) => value && value !== null),
          take(1),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((data) => {
          data.forEach((value) => {
            if (value.id === this.pastDataId) {
              this.restoreFormData(value);
            }
          });
        });
    }
  }

  /**
   * Sets the value of the form fields if form is used to edit a previously saved traveller's
   * details.
   * @param data - traveller data to be edited
   */
  public restoreFormData(data: IMemberProfileTraveller): void {
    this.travellerFormGroup.get('title').setValue(data.title);
    this.travellerFormGroup.get('first_name').setValue(data.first_name);
    this.travellerFormGroup.get('last_name').setValue(data.last_name);
    this.travellerFormGroup.get('gender').setValue(data.gender);
    this.travellerFormGroup.get('citizenship').setValue(data.citizenship.name);
    this.travellerFormGroup.get('country_url').setValue(data.citizenship.url);
    this.travellerFormGroup.get('birth_date').setValue(moment(data?.birth_date).toISOString());
  }

  /**
   * Called once the user chooses a value from the citizenship autocomplete
   * @param country - The url value of the country chosen
   */
  public setChosenCountry(country: string): void {
    this.travellerFormGroup.controls['country_url'].setValue(country);
  }

  /**
   * Resets the citizenship form control if entered value is invalid. Called on citizenship input blur
   */
  public checkCitizenship(): void {
    if (this.invalidCitizenship || this.travellerFormGroup.controls['country_url'].value === '') {
      this.travellerFormGroup.controls['citizenship'].setValue('');
      this.travellerFormGroup.controls['country_url'].setValue('');
    }
  }

  /**
   * Filters the total list of countries to find country names that contain characters entered by the user.
   * Called each time the citizenship value changes
   */
  public filterAutocomplete(): void {
    const filterValue = this.travellerFormGroup.controls['citizenship'].value.toLowerCase().trim();

    this.filteredCountries = this.countries.filter((country) => country.name.toLowerCase().includes(filterValue));
    this.invalidCitizenship = this.filteredCountries.length === 0 ? true : false;
    if (this.filteredCountries.length > 1) {
      this.travellerFormGroup.controls['country_url'].setValue('');
    }
  }

  /**
   * Close the traveller form by emitting a value that is caught by the popupFormComponent. Called when the user
   * clicks onto the close button.
   */
  public closeForm(): void {
    this.closed.emit();
  }

  /**
   * Dispatch the relevant action to delete the current traveller details. Called when the user clicks onto the
   * remove button.
   */
  public removeTraveller(): void {
    this.ngrxStore.dispatch(new MemberProfileStore.DestroyProfileTraveller(this.pastDataId));
    this.closeForm();
  }

  /**
   * Checks for form validation before dispatching the relevant action to either add or update a traveller's details.
   * Called when the user clicks onto the submit button.
   */
  public submitForm(): void {
    this.angulartics2Segment.eventTrack('My profile traveller details submitted', {});

    const data = this.travellerFormGroup.value;
    if (this.travellerFormGroup.valid) {
      this.closeForm();
      data['birth_date'] = this.datePipe.transform(data['birth_date'], 'yyyy-MM-dd');
      data['member_profile'] = this.memberUrl;
      data['citizenship'] = data['country_url'];
      delete data['country_url'];

      this.util.stripHTMLFromObject(data);

      if (this.newTraveller) {
        this.ngrxStore.dispatch(new MemberProfileStore.CreateProfileTraveller(data));
      } else {
        this.ngrxStore.dispatch(new MemberProfileStore.UpdateProfileTraveller({ id: this.pastDataId, data }));
      }
    } else {
      Object.keys(this.travellerFormGroup.controls).forEach((field) => {
        this.travellerFormGroup.controls[field].markAsTouched();
      });
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
